import React, { useState, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { isSSR, validate, getLangDir } from 'wix-rich-content-common';
import verticalEmbedSchema from 'wix-rich-content-common/dist/statics/schemas/vertical-embed.schema.json';
import classNames from 'classnames';
import { Node_Type } from 'ricos-schema';
import { isNumber } from 'lodash';
import Tooltip from 'wix-rich-content-common/libs/Tooltip';
import { getScaleImageSrc } from 'wix-rich-content-common/libs/imageUtils';

const VERTICAL_EMBED_TYPE = "wix-draft-plugin-vertical-embed";

var styles = {"card":"F-coD","link":"G42mM","container":"gw9zJ","image":"zLApA","right":"bs8Yg","cardLayout":"IN1PG","content":"WPsFw","title":"_2AJ61","subtitle":"rEW85","button":"Fdvi-","button_disabled":"_1VQtZ","buttonText":"bAURk","slimLayout":"fIaT0","rtl":"jYKdK","ltr":"b5bYn"};

const verticalEmbedProviders = { event: "event", booking: "booking", product: "product" };
({
  insertBooking: `${Node_Type.APP_EMBED}.insertBooking`,
  insertProduct: `${Node_Type.APP_EMBED}.insertProduct`,
  insertEvent: `${Node_Type.APP_EMBED}.insertEvent`,
  replace: `${Node_Type.APP_EMBED}.replace`
});

const convertDuration = (durationInMinutes, t) => {
  if (!isNumber(durationInMinutes))
    return "";
  if (durationInMinutes < 60) {
    return t("VerticalEmbed_Units_Minute", { minutes: durationInMinutes });
  }
  const hours = durationInMinutes / 60;
  const rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);
  const durationInHours = rminutes === 0 ? t("VerticalEmbed_Units_Hour", { hours: rhours }) : t("VerticalEmbed_Units_Hour", { hours: rhours }) + " " + t("VerticalEmbed_Units_Minute", { minutes: rminutes });
  return durationInHours;
};

const getBookingContent = ({ name, durations }, t) => ({
  title: name,
  info: { leftSubtitle: durations && convertDuration(durations, t) },
  buttonText: t("VerticalEmbed_Bookings_Button")
});
const getEventContent = ({ name, scheduling, location }, t) => ({
  title: name,
  info: { leftSubtitle: scheduling, rightSubtitle: location },
  buttonText: t("VerticalEmbed_Events_Button")
});
const getProductContent = ({ name }, t) => ({
  title: name,
  buttonText: t("VerticalEmbed_Products_Button")
});
const contentTypeMapGetter = {
  [verticalEmbedProviders.booking]: getBookingContent,
  [verticalEmbedProviders.event]: getEventContent,
  [verticalEmbedProviders.product]: getProductContent
};
const verticalsDataBuilder = (type, data, t, changeBaseUrl = (x) => x) => {
  const { imageSrc, pageUrl, imageWidth, imageHeight } = data;
  return {
    url: changeBaseUrl(pageUrl),
    imageSrc,
    imageWidth,
    imageHeight,
    content: contentTypeMapGetter[type](data, t)
  };
};

const subtitle = (info) => {
  return info.leftSubtitle && /* @__PURE__ */ React.createElement("div", { className: styles.subtitle }, info.leftSubtitle, info.rightSubtitle && /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement("span", { className: styles.right }, "|"), info.rightSubtitle));
};
const ellipsisTitle = (title) => {
  return title.length <= 80 ? title : `${title.substring(0, 80)}...`;
};
const CardContent = (props) => {
  const { title, info, buttonText, disabled, t } = props;
  return /* @__PURE__ */ React.createElement("div", { className: styles.content }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", { className: styles.title }, ellipsisTitle(title)), info && subtitle(info)), /* @__PURE__ */ React.createElement(Tooltip, { content: disabled ? t("VerticalEmbed_Disabled_Button_Tooltip") : null }, /* @__PURE__ */ React.createElement("div", { className: classNames(styles.button, { [styles.button_disabled]: disabled }) }, /* @__PURE__ */ React.createElement("div", { className: styles.buttonText }, buttonText))));
};
CardContent.propTypes = {
  title: PropTypes.string.isRequired,
  info: PropTypes.object,
  buttonText: PropTypes.string.isRequired,
  disabled: PropTypes.string.boolean,
  t: PropTypes.func.isRequired
};

var __defProp$2 = Object.defineProperty;
var __defProps$1 = Object.defineProperties;
var __getOwnPropDescs$1 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$2 = Object.getOwnPropertySymbols;
var __hasOwnProp$2 = Object.prototype.hasOwnProperty;
var __propIsEnum$2 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$2 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$2.call(b, prop))
      __defNormalProp$2(a, prop, b[prop]);
  if (__getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(b)) {
      if (__propIsEnum$2.call(b, prop))
        __defNormalProp$2(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$1 = (a, b) => __defProps$1(a, __getOwnPropDescs$1(b));
const getImageTargetHeight = (width, height, targetWidth) => {
  const proportion = height / width;
  const targetHeight = proportion * targetWidth;
  return targetHeight;
};
const Card = (props) => {
  const { url, content, direction, imageWidth, imageHeight, t, disabled } = props;
  const [imageSrc, setImageSrc] = useState();
  const getImageSrc = (targetWidth) => {
    const targetHeight = getImageTargetHeight(imageWidth, imageHeight, targetWidth);
    return getScaleImageSrc(props.imageSrc, targetWidth, targetHeight);
  };
  const setRef = (ref) => {
    var _a;
    if (ref) {
      const targetWidth = (_a = ref.getBoundingClientRect()) == null ? void 0 : _a.width;
      if (imageWidth && imageHeight) {
        const imageSrc2 = getImageSrc(targetWidth);
        setImageSrc(imageSrc2);
      } else {
        setImageSrc(props.imageSrc);
      }
    }
  };
  const imageUrl = isSSR() ? imageWidth && imageHeight ? getImageSrc(185) : props.imageSrc : imageSrc;
  const imageStyle = imageUrl ? { backgroundImage: `url(${imageUrl})` } : {};
  return (
    // eslint-disable-next-line react/jsx-no-target-blank
    /* @__PURE__ */ React.createElement("a", { className: styles.link, href: disabled ? null : url, target: "_blank", ref: setRef }, /* @__PURE__ */ React.createElement(
      "div",
      {
        style: { direction },
        className: classNames(styles[direction], styles.container, styles.cardLayout)
      },
      props.imageSrc && /* @__PURE__ */ React.createElement("div", { style: imageStyle, className: styles.image }),
      /* @__PURE__ */ React.createElement(CardContent, __spreadProps$1(__spreadValues$2({}, content), { disabled, t }))
    ))
  );
};
Card.propTypes = {
  imageSrc: PropTypes.string,
  content: PropTypes.object,
  direction: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  imageWidth: PropTypes.string,
  imageHeight: PropTypes.string,
  disabled: PropTypes.boolean,
  t: PropTypes.func.isRequired
};

var __defProp$1 = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __publicField = (obj, key, value) => {
  __defNormalProp$1(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class VerticalEmbedComponent extends PureComponent {
  constructor(props) {
    super(props);
    __publicField(this, "onClick", () => {
      var _a, _b;
      return (_b = (_a = this.props.helpers).onViewerAction) == null ? void 0 : _b.call(
        _a,
        VERTICAL_EMBED_TYPE,
        "Click",
        this.props.componentData.type
      );
    });
    validate(props.componentData, verticalEmbedSchema);
  }
  render() {
    const {
      componentData,
      className,
      settings: { slimLayout = false, changeBaseUrl, disabled = false },
      t,
      locale
    } = this.props;
    const { selectedProduct, type } = componentData;
    const props = verticalsDataBuilder(type, selectedProduct, t, changeBaseUrl);
    const direction = getLangDir(locale);
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: classNames(className, styles.card, { [styles.slimLayout]: slimLayout }),
          "data-hook": "vertical-embed",
          onClick: this.onClick
        },
        /* @__PURE__ */ React.createElement(Card, __spreadProps(__spreadValues$1({}, props), { t, direction, disabled }))
      )
    );
  }
}
VerticalEmbedComponent.propTypes = {
  componentData: PropTypes.object.isRequired,
  className: PropTypes.string,
  settings: PropTypes.object,
  helpers: PropTypes.object,
  locale: PropTypes.string,
  t: PropTypes.func
};
var VerticalEmbedComponent$1 = VerticalEmbedComponent;

const typeMapper = () => ({
  [VERTICAL_EMBED_TYPE]: { component: VerticalEmbedComponent$1 }
});

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
const pluginVerticalEmbed = (config) => {
  return {
    config: __spreadValues({}, config),
    type: VERTICAL_EMBED_TYPE,
    typeMapper
    // nodeViewRenderers,
  };
};

export { VERTICAL_EMBED_TYPE, pluginVerticalEmbed, typeMapper as verticalEmbedTypeMapper };
//# sourceMappingURL=viewer.js.map

import 'wix-rich-content-plugin-vertical-embed/dist/es/styles.viewer.min.global.css'